import { useEffect, useState } from 'react';
import { BrandLink } from '../../_Shared/BrandLink';
import { FaBars, FaTimes } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import { NavList } from './NavList';
import { NavPanel } from './NavPanel';
import { useLanguage } from '../../../../Hooks/Contexts/Translate/useLanguageContext';
import { onResolveHeaderTemplate } from '../_HeaderTemplates';
import { HEADER_TEMPLATE_EVENT, onReadHeaderTemplate } from '../../../AppTemplateResolved';

const BLOCK = 'LStudio__app_header';

const MOBILE_BREAKPOINT = 900;

/*
    🔥 EL HEADER, UNA SOLA VEZ

    Público  -> marca + navegación + iconos
    Privado  -> SOLO iconos (la marca y la nav viven en el sidebar),
                más el botón que abre el sidebar en mobile.

    ⚠️ Los iconos van FUERA de la nav: no colapsan nunca.
*/

export const HeaderShell = ({
    templateId,
    isPrivate = false,
    brand = null,
    homePath = '/',
    navItems = [],
    utilities = null,
    extraTop = null,
    extraType = 'ad',
    onToggleAside = null,
}) => {

    const SCOPE = isPrivate ? 'private' : 'public';
    const SLOT = isPrivate ? brand?.headerPrivate : brand?.headerPublic;

    const [useTemplateId, setTemplateId] = useState(() => onReadHeaderTemplate(SCOPE, templateId));
    const { useDataLanguage } = useLanguage();

    const NAV_TEXT = useDataLanguage.translations?.TEMPLATES?.nav ?? {};

    const [useIsOpen, setIsOpen] = useState(false);
    const [useIsMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);

    const TEMPLATE = onResolveHeaderTemplate(useTemplateId);

    const { pathname } = useLocation();

    // 🔥 Cambio de disposición en caliente (panel de prueba)
    useEffect(() => {

        const onChange = (event) => {
            if (event.detail?.scope !== SCOPE) return;
            setTemplateId(event.detail.id);
            setIsOpen(false);
        };

        window.addEventListener(HEADER_TEMPLATE_EVENT, onChange);
        return () => window.removeEventListener(HEADER_TEMPLATE_EVENT, onChange);

    }, [SCOPE]);

    // 🔥 Cierra al navegar
    useEffect(() => { setIsOpen(false); }, [pathname]);

    // 🔥 Cierra al cruzar el breakpoint
    useEffect(() => {

        const onResize = () => {
            const IS_MOBILE = window.innerWidth < MOBILE_BREAKPOINT;
            setIsMobile((prev) => { if (prev !== IS_MOBILE) setIsOpen(false); return IS_MOBILE; });
        };

        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);

    }, []);

    // 🔥 Cierra con Escape
    useEffect(() => {

        const onEscape = (event) => { if (event.key === 'Escape') setIsOpen(false); };

        document.addEventListener('keydown', onEscape);
        return () => document.removeEventListener('keydown', onEscape);

    }, []);

    const HAS_NAV = navItems.length > 0;

    // Colapsa en mobile siempre; en escritorio solo si el template lo pide.
    const IS_COLLAPSED = HAS_NAV && (useIsMobile || TEMPLATE.always || TEMPLATE.nav === 'hidden');

    const SHOW_INLINE_NAV = HAS_NAV && !IS_COLLAPSED && TEMPLATE.nav !== 'row_below';

    const SHOW_ROW_NAV = HAS_NAV && !IS_COLLAPSED && TEMPLATE.nav === 'row_below';

    return (
        <header className={`${BLOCK} ${isPrivate ? `${BLOCK}--private` : ''}`}
            data-header={TEMPLATE.id}
            data-brand={TEMPLATE.brand}
            data-nav={TEMPLATE.nav}
            data-collapse={TEMPLATE.collapse}
            data-collapsed={IS_COLLAPSED} >

            {extraTop && <div className={`${BLOCK}__extra`} data-extra={extraType}>{extraTop}</div>}

            <div className={`${BLOCK}__bar`}>

                {
                    onToggleAside && (
                        <button type="button" className={`${BLOCK}__aside_toggle`}
                            aria-label={NAV_TEXT.openAside} onClick={onToggleAside} >
                            <FaBars />
                        </button>
                    )
                }

                {
                    brand && (
                        <BrandLink slot={SLOT} homePath={homePath} className={`${BLOCK}__brand`} isLogo={SLOT?.isLogo === true}>
                            {SLOT?.isLogo && SLOT?.logoLight && (
                                <img src={SLOT.logoLight} alt={SLOT?.alt ?? ""} title={SLOT?.title ?? ""}
                                    className={`${BLOCK}__logo ${BLOCK}__logo--light`}
                                    style={SLOT?.imageStyle}
                                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            )}

                            {SLOT?.isLogo && SLOT?.logoDark && (
                                <img src={SLOT.logoDark} alt={SLOT?.alt ?? ""} title={SLOT?.title ?? ""}
                                    className={`${BLOCK}__logo ${BLOCK}__logo--dark`}
                                    style={SLOT?.imageStyle}
                                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            )}

                            {!SLOT?.isLogo && <span className={`${BLOCK}__brand_name`}>{brand?.name}</span>}

                        </BrandLink>
                    )
                }

                {
                    SHOW_INLINE_NAV && (
                        <nav className={`${BLOCK}__nav`} aria-label={NAV_TEXT.main}>
                            <NavList items={navItems} />
                        </nav>
                    )
                }

                {/* 🔥 SIEMPRE VISIBLE — no colapsa en ningún template ni resolución */}
                <div className={`${BLOCK}__actions`}>{utilities}</div>

                {
                    IS_COLLAPSED && (
                        <button type="button" className={`${BLOCK}__toggle`}
                            aria-label={useIsOpen ? NAV_TEXT.closeMenu : NAV_TEXT.openMenu}
                            aria-expanded={useIsOpen}
                            onClick={() => setIsOpen(!useIsOpen)} >
                            {useIsOpen ? <FaTimes /> : <FaBars />}
                        </button>
                    )
                }

            </div>

            {
                SHOW_ROW_NAV && (
                    <nav className={`${BLOCK}__row`} aria-label={NAV_TEXT.main}>
                        <NavList items={navItems} />
                    </nav>
                )
            }

            {
                IS_COLLAPSED && (
                    <NavPanel mode={TEMPLATE.collapse} title={NAV_TEXT.menu} isOpen={useIsOpen}
                        onClose={() => setIsOpen(false)} items={navItems} />
                )
            }

        </header>
    );
};
